/*
 * wfy-delivery-lookup.js
 * -----------------------
 * Shared delivery-zone lookup used by BOTH:
 *   - the standalone postcode-checker banner (postaljs.js)
 *   - the cart suburb picker (wfy-suburb-picker.js)
 *
 * One code path = the two surfaces can never disagree.
 *
 * Data source: wfy-delivery-data.json
 *   {
 *     "zones":     { "VMMET/MMI": { "windows": ["...", "...", "..."] }, ... },
 *     "postcodes": { "3006": { "SOUTHBANK": { "zone": "VMMET/MMI", "peLocation": "3006B" } } }
 *   }
 *
 * Usage:
 *   WFYDelivery.load(url).then(function () {
 *     var suburbs = WFYDelivery.getSuburbs('3006');          // ["SOUTH WHARF", "SOUTHBANK"]
 *     var info    = WFYDelivery.resolve('3006', 'SOUTHBANK'); // { zone, windows, peLocation }
 *   });
 */
(function () {
  'use strict';
  if (window.WFYDelivery) return;

  var data = null;
  var dataPromise = null;

  // load(url): fetch the JSON once. Idempotent — concurrent/later callers
  // share the same in-flight promise, so whichever surface loads first wins
  // and the other reuses it (browser also caches the asset).
  function load(url) {
    if (dataPromise) return dataPromise;
    dataPromise = fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('wfy-delivery-data.json HTTP ' + r.status);
        return r.json();
      })
      .then(function (json) { data = json; return json; })
      .catch(function (err) {
        // Reset so a later caller can retry, but surface the error.
        dataPromise = null;
        throw err;
      });
    return dataPromise;
  }

  function ready() {
    return dataPromise || Promise.reject(new Error('WFYDelivery.load(url) has not been called yet'));
  }

  function isLoaded() { return !!data; }

  function normPostcode(pc) {
    return String(pc == null ? '' : pc).trim();
  }

  function normSuburbKey(s) {
    return String(s == null ? '' : s).trim().toLowerCase();
  }

  // True if we deliver to this postcode at all.
  function isServed(postcode) {
    if (!data || !data.postcodes) return false;
    return !!data.postcodes[normPostcode(postcode)];
  }

  // Suburb display names for a postcode, sorted A–Z. [] if postcode not served.
  function getSuburbs(postcode) {
    if (!data || !data.postcodes) return [];
    var entry = data.postcodes[normPostcode(postcode)];
    if (!entry) return [];
    return Object.keys(entry).sort(function (a, b) {
      return a.localeCompare(b);
    });
  }

  // resolve(postcode, suburb) -> { zone, windows, peLocation } or null.
  // Matches the suburb exactly first, then case-insensitively as a fallback.
  function resolve(postcode, suburb) {
    if (!data || !data.postcodes) return null;
    var entry = data.postcodes[normPostcode(postcode)];
    if (!entry) return null;

    var rec = entry[suburb];
    if (!rec) {
      var want = normSuburbKey(suburb);
      var match = Object.keys(entry).filter(function (name) {
        return normSuburbKey(name) === want;
      })[0];
      rec = match ? entry[match] : null;
    }
    if (!rec) return null;

    var zoneInfo = (data.zones && data.zones[rec.zone]) || { windows: [] };
    return {
      zone: rec.zone,
      windows: (zoneInfo.windows || []).slice(),
      peLocation: rec.peLocation
    };
  }

  window.WFYDelivery = {
    load: load,
    ready: ready,
    isLoaded: isLoaded,
    isServed: isServed,
    getSuburbs: getSuburbs,
    resolve: resolve
  };
})();
